// src/components/Widgets/AnalyticsWidget.jsx
import React, { useContext, useMemo } from "react";
import WidgetHeader from "../Common/WidgetHeader";
import { useMarketing } from "../../contexts/MarketingContext";
import { UtilitiesContext } from "../../contexts/UtilitiesContext";
import { useStudio } from "../../contexts/StudioContext";

const fmt = (v, currency = "AUD", locale = "en-AU") => {
  const n = Number(v) || 0;
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(n);
  } catch {
    return `A$${n.toFixed(2)}`;
  }
};

export default function AnalyticsWidget({
  settings,
  backTo = "/dashboard",
  currency: currencyProp,   // optional override
  locale = "en-AU",
}) {
  // Providers may be absent in some routes; guard access
  const marketing = (typeof useMarketing === "function" ? useMarketing() : null) || {};
  const { monthlyTotal: marketingMonthlyTotalRaw } = marketing;

  const utilitiesCtx = useContext(UtilitiesContext) || {};
  const utilities = Array.isArray(utilitiesCtx.utilities) ? utilitiesCtx.utilities : [];

  let studio;
  try {
    studio = useStudio(); // may throw if provider missing
  } catch {
    studio = null;
  }

  const currency =
    currencyProp ||
    studio?.settings?.currency ||
    settings?.currency ||
    "AUD";

  const unitsPerMonth = useMemo(() => {
    const studioExpected = Number(studio?.expectedOutput);
    if (Number.isFinite(studioExpected) && studioExpected >= 0) return studioExpected;
    const legacy = Number(settings?.productionOutput || 0);
    return Number.isFinite(legacy) && legacy >= 0 ? legacy : 0;
  }, [studio?.expectedOutput, settings?.productionOutput]);

  const utilitiesMonthlyTotal = useMemo(() => {
    return utilities.reduce((sum, u) => {
      const c = Number(u?.cost) || 0;
      switch (u?.frequency) {
        case "Weekly":      return sum + c * 4.333;
        case "Fortnightly": return sum + c * 2.166;
        case "Monthly":     return sum + c;
        case "Quarterly":   return sum + c / 3;
        case "Yearly":      return sum + c / 12;
        default:            return sum + c; // treat unknown as monthly
      }
    }, 0);
  }, [utilities]);

  const marketingMonthlyTotal = Number(marketingMonthlyTotalRaw) || 0;

  const marketingPerUnit = unitsPerMonth > 0 ? marketingMonthlyTotal / unitsPerMonth : 0;
  const utilitiesPerUnit = unitsPerMonth > 0 ? utilitiesMonthlyTotal / unitsPerMonth : 0;

  const PerUnit = ({ label, value }) => (
    <div className="rounded-lg p-4 bg-white/5 border border-white/10">
      <div className="text-xs opacity-70 mb-1">{label}</div>
      <div className="text-2xl font-bold">
        {unitsPerMonth > 0 ? fmt(value, currency, locale) : "—"}
      </div>
      <div className="text-xs opacity-60 mt-1">
        Based on Units / month ({unitsPerMonth || 0})
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      <WidgetHeader
        to={backTo}
        title={
          <div className="flex items-center gap-2">
            <span role="img" aria-label="analytics"></span>
            <span>Studio Analytics</span>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="rounded-lg p-4 bg-white/5 border border-white/10">
          <div className="text-xs opacity-70 mb-1">Units / month</div>
          <div className="text-2xl font-bold tabular-nums">{unitsPerMonth}</div>
          <div className="text-xs opacity-60 mt-1">
            {studio ? "From Studio Profile" : "From Settings (fallback)"}
          </div>
        </div>

        <div className="rounded-lg p-4 bg-white/5 border border-white/10">
          <div className="text-xs opacity-70 mb-1">Marketing (monthly)</div>
          <div className="text-2xl font-bold">{fmt(marketingMonthlyTotal, currency, locale)}</div>
          <div className="text-xs opacity-60 mt-1">Sum of active campaigns</div>
        </div>

        <div className="rounded-lg p-4 bg-white/5 border border-white/10">
          <div className="text-xs opacity-70 mb-1">Utilities (monthly)</div>
          <div className="text-2xl font-bold">{fmt(utilitiesMonthlyTotal, currency, locale)}</div>
          <div className="text-xs opacity-60 mt-1">Normalised to monthly</div>
        </div>
      </div>

      {/* Per-unit */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PerUnit label="Marketing per unit" value={marketingPerUnit} />
        <PerUnit label="Utilities per unit" value={utilitiesPerUnit} />
      </div>

      <p className="text-xs opacity-70">
        Tip: Update <span className="font-semibold">Marketing</span> campaigns and{" "}
        <span className="font-semibold">Utilities</span> to keep these numbers fresh.
      </p>
    </div>
  );
}
